import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// @ts-ignore - react-syntax-highlighter types are complex
import oneDark from 'react-syntax-highlighter/dist/esm/styles/prism/one-dark';
import { ArrowLeft, FileText, Clock } from 'lucide-react';

interface DocumentationViewerProps {
  title: string;
  description: string;
  location: string;
  markdown: string;
  onBack: () => void;
}

interface Heading {
  id: string;
  level: number;
  text: string;
}

export const DocumentationViewer: React.FC<DocumentationViewerProps> = ({
  title,
  description,
  location,
  markdown,
  onBack,
}) => {
  // Extract headings for table of contents - matches the same ID generation as render
  const headings = useMemo(() => {
    const headingRegex = /^(#{1,6})\s+(.+)$/gm;
    const matches: Heading[] = [];
    let match;

    while ((match = headingRegex.exec(markdown)) !== null) {
      const level = match[1].length;
      let text = match[2].trim();
      
      // Strip markdown formatting from heading text (bold, italic, links, etc.)
      // Remove markdown links: [text](url) -> text
      text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
      // Remove markdown bold/italic: **text** or *text* -> text
      text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
      text = text.replace(/\*([^*]+)\*/g, '$1');
      text = text.replace(/__([^_]+)__/g, '$1');
      text = text.replace(/_([^_]+)_/g, '$1');
      // Remove inline code: `code` -> code
      text = text.replace(/`([^`]+)`/g, '$1');
      
      // Generate ID using same algorithm as generateHeadingId
      const id = text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      matches.push({ id, level, text });
    }

    return matches;
  }, [markdown]);

  // Generate anchor links for headings - matches GitHub's heading ID algorithm
  const generateHeadingId = (text: string | React.ReactNode): string => {
    // Extract plain text from React nodes
    let plainText = '';
    if (typeof text === 'string') {
      plainText = text;
    } else if (Array.isArray(text)) {
      plainText = text
        .map((node) => {
          if (typeof node === 'string') return node;
          if (React.isValidElement(node) && node.props?.children) {
            return generateHeadingId(node.props.children);
          }
          return '';
        })
        .join('');
    } else if (React.isValidElement(text) && text.props?.children) {
      plainText = generateHeadingId(text.props.children);
    }
    
    // GitHub-style heading ID generation
    return plainText
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special chars except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  return (
    <div className="min-h-screen bg-charcoal-50 overflow-x-hidden">
      {/* Header Bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-charcoal-200 shadow-sm">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Documentation Hub
            </button>
            <div className="flex items-center gap-3 text-sm text-charcoal-600">
              <FileText className="h-4 w-4" />
              <span className="font-mono text-xs truncate max-w-xs">{location}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8 overflow-x-visible">
          {/* Sidebar - Table of Contents (desktop) */}
          {headings.length > 0 && (
            <aside className="hidden lg:block w-64 flex-shrink-0 min-w-0">
              <div className="sticky top-24">
                <div className="bg-white rounded-lg border border-charcoal-200 p-4 shadow-sm max-h-[calc(100vh-8rem)] overflow-y-auto overflow-x-visible">
                  <h3 className="text-sm font-semibold text-charcoal-900 mb-3 font-heading sticky top-0 bg-white pb-2 z-10">
                    On this page
                  </h3>
                  <nav className="space-y-1">
                    {headings.map((heading, index) => {
                      const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        if (!heading.id?.trim()) return;
                        const element = document.getElementById(heading.id);
                        if (!element) return;
                        
                        // Get the clicked link's position in the viewport (where user clicked)
                        const linkRect = e.currentTarget.getBoundingClientRect();
                        const linkViewportY = linkRect.top;
                        
                        // Get the target heading's absolute position in the document
                        const headingRect = element.getBoundingClientRect();
                        const headingAbsoluteY = headingRect.top + window.pageYOffset;
                        
                        // Calculate scroll target: position heading where link is in viewport
                        // This keeps the sidebar visible and aligns the heading with the clicked item
                        const scrollTarget = headingAbsoluteY - linkViewportY;
                        
                        // Scroll smoothly to align heading with clicked TOC item
                        window.scrollTo({
                          top: Math.max(0, scrollTarget),
                          behavior: 'smooth'
                        });
                        
                        // Update URL hash for deep linking
                        window.history.pushState(null, '', `#${heading.id}`);
                      };
                      
                      return (
                        <a
                          key={`${heading.id}-${index}`}
                          href={`#${heading.id}`}
                          onClick={handleClick}
                          className={`block text-sm transition-colors break-words cursor-pointer hover:text-primary-600 ${
                            heading.level === 1
                              ? 'font-semibold text-charcoal-900'
                              : heading.level === 2
                              ? 'font-medium text-charcoal-700 pl-2'
                              : 'text-charcoal-600 pl-4 text-xs'
                          }`}
                          title={heading.text}
                          style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                        >
                          {heading.text}
                        </a>
                      );
                    })}
                  </nav>
                </div>
              </div>
            </aside>
          )}

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <article className="bg-white rounded-lg border border-charcoal-200 shadow-sm overflow-hidden">
              {/* Document Header */}
              <div className="border-b border-charcoal-200 bg-charcoal-50 px-8 py-6">
                <h1 className="text-4xl font-bold text-charcoal-900 mb-2 font-heading">
                  {title}
                </h1>
                {description && (
                  <p className="text-lg text-charcoal-600 mb-3 font-body">
                    {description}
                  </p>
                )}
                <div className="flex items-center gap-4 text-sm text-charcoal-500">
                  <span className="font-mono">{location}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>Production documentation</span>
                  </span>
                </div>
              </div>

              {/* Document Body */}
              <div className="px-8 py-8">
                <div className="prose prose-lg max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    components={{
                      // Headings with anchor links
                      h1: ({ node, children, ...props }) => {
                        const id = generateHeadingId(children);
                        return (
                          <h1
                            id={id}
                            className="text-4xl font-bold text-charcoal-900 mt-10 mb-4 pb-3 border-b border-charcoal-200 font-heading first:mt-0 scroll-mt-24"
                            {...props}
                          >
                            {children}
                          </h1>
                        );
                      },
                      h2: ({ node, children, ...props }) => {
                        const id = generateHeadingId(children);
                        return (
                          <h2
                            id={id}
                            className="text-3xl font-semibold text-charcoal-900 mt-8 mb-4 pb-2 border-b border-charcoal-100 font-heading scroll-mt-24"
                            {...props}
                          >
                            {children}
                          </h2>
                        );
                      },
                      h3: ({ node, children, ...props }) => {
                        const id = generateHeadingId(children);
                        return (
                          <h3
                            id={id}
                            className="text-2xl font-semibold text-charcoal-900 mt-6 mb-3 font-heading scroll-mt-24"
                            {...props}
                          >
                            {children}
                          </h3>
                        );
                      },
                      h4: ({ node, children, ...props }) => {
                        const id = generateHeadingId(children);
                        return (
                          <h4
                            id={id}
                            className="text-xl font-semibold text-charcoal-900 mt-4 mb-2 font-heading scroll-mt-24"
                            {...props}
                          >
                            {children}
                          </h4>
                        );
                      },
                      h5: ({ node, children, ...props }) => {
                        const id = generateHeadingId(children);
                        return (
                          <h5
                            id={id}
                            className="text-lg font-semibold text-charcoal-900 mt-4 mb-2 font-heading scroll-mt-24"
                            {...props}
                          >
                            {children}
                          </h5>
                        );
                      },
                      h6: ({ node, children, ...props }) => {
                        const id = generateHeadingId(children);
                        return (
                          <h6
                            id={id}
                            className="text-base font-semibold text-charcoal-900 mt-3 mb-2 font-heading scroll-mt-24"
                            {...props}
                          >
                            {children}
                          </h6>
                        );
                      },
                      // Paragraphs
                      p: ({ node, ...props }) => (
                        <p
                          className="text-charcoal-700 mb-4 leading-relaxed font-body text-base"
                          {...props}
                        />
                      ),
                      // Lists
                      ul: ({ node, ...props }) => (
                        <ul
                          className="list-disc list-outside mb-4 space-y-2 ml-6 text-charcoal-700 font-body"
                          {...props}
                        />
                      ),
                      ol: ({ node, ...props }) => (
                        <ol
                          className="list-decimal list-outside mb-4 space-y-2 ml-6 text-charcoal-700 font-body"
                          {...props}
                        />
                      ),
                      li: ({ node, ...props }) => (
                        <li className="pl-2 font-body" {...props} />
                      ),
                      // Inline code
                      code: ({ node, inline, className, children, ...props }: any) => {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : '';
                        
                        if (inline) {
                          return (
                            <code
                              className="px-1.5 py-0.5 bg-charcoal-100 text-charcoal-800 rounded text-sm font-mono border border-charcoal-200"
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        }

                        return (
                          <SyntaxHighlighter
                            language={language || 'text'}
                            style={oneDark}
                            customStyle={{
                              margin: '1rem 0',
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem',
                              lineHeight: '1.5',
                            }}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        );
                      },
                      // Pre blocks (fallback)
                      pre: ({ node, ...props }) => (
                        <pre className="mb-4" {...props} />
                      ),
                      // Blockquotes
                      blockquote: ({ node, ...props }) => (
                        <blockquote
                          className="border-l-4 border-primary-500 pl-4 py-2 my-4 italic text-charcoal-600 bg-charcoal-50 rounded-r font-body"
                          {...props}
                        />
                      ),
                      // Links
                      a: ({ node, href, ...props }: any) => (
                        <a
                          href={href}
                          target={href?.startsWith('http') ? '_blank' : undefined}
                          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-primary-600 hover:text-primary-700 underline font-medium transition-colors"
                          {...props}
                        />
                      ),
                      // Tables
                      table: ({ node, ...props }) => (
                        <div className="overflow-x-auto my-6 -mx-4 sm:mx-0">
                          <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden border border-charcoal-200 rounded-lg">
                              <table className="min-w-full divide-y divide-charcoal-200" {...props} />
                            </div>
                          </div>
                        </div>
                      ),
                      thead: ({ node, ...props }) => (
                        <thead className="bg-charcoal-50" {...props} />
                      ),
                      tbody: ({ node, ...props }) => (
                        <tbody className="bg-white divide-y divide-charcoal-200" {...props} />
                      ),
                      tr: ({ node, ...props }) => (
                        <tr className="hover:bg-charcoal-50 transition-colors" {...props} />
                      ),
                      th: ({ node, ...props }) => (
                        <th
                          className="px-4 py-3 text-left text-xs font-semibold text-charcoal-900 uppercase tracking-wider font-heading"
                          {...props}
                        />
                      ),
                      td: ({ node, ...props }) => (
                        <td
                          className="px-4 py-3 text-sm text-charcoal-700 font-body whitespace-nowrap"
                          {...props}
                        />
                      ),
                      // Horizontal rules
                      hr: ({ node, ...props }) => (
                        <hr className="my-8 border-t border-charcoal-200" {...props} />
                      ),
                      // Images
                      img: ({ node, src, alt, ...props }: any) => (
                        <img
                          src={src}
                          alt={alt}
                          className="max-w-full h-auto rounded-lg border border-charcoal-200 my-4 shadow-sm"
                          {...props}
                        />
                      ),
                    }}
                  >
                    {markdown}
                  </ReactMarkdown>
                </div>
              </div>
            </article>
          </main>
        </div>
      </div>
    </div>
  );
};

